import React, { useEffect, useRef, useState } from 'react'
import { css } from 'src/ui/style/css'
import { useTheme } from '@emotion/react'
import { Icon } from 'src/ui/icon/Icon'
import { icon } from 'src/ui/icon/icons'
import {
    notifyToastOpen,
    notifyToastClose,
    INFO_TOAST_STATE_CHANGE,
    tonePriority
} from './infoOverlayBus'
import { createPortal } from 'react-dom'
import { getToastStackRoot } from './toastStackRoot'
import { isMobile } from 'src/geo/locationService'

export type InfoTone = 'warning' | 'info' | 'success' | 'error'
export type InfoKind =
    | 'system'
    | 'transaction'
    | 'marketing'
    | 'educational'
    | 'context'

export type InfoCta = {
    label: string
    onClick: () => void
    ariaLabel?: string
}

export type InfoToasterProps = {
    open: boolean
    title: React.ReactNode
    text: React.ReactNode
    onClose: () => void
    tone?: InfoTone
    kind?: InfoKind
    /** When true, Esc will not close the toaster. */
    disableEscapeClose?: boolean
    /** Provide custom icon; otherwise inferred from tone/kind */
    customIcon?: React.ReactNode
    /** Primary CTA shown prominently; secondary CTAs render as links */
    primaryCta?: InfoCta
    secondaryCtas?: InfoCta[]
    /** When true, clicking on overlay closes the toaster */
    'data-testid'?: string
}

const InfoToaster: React.FC<InfoToasterProps> = ({
    open,
    title,
    text,
    onClose,
    tone = 'info',
    kind = 'system',
    disableEscapeClose,
    customIcon,
    primaryCta,
    secondaryCtas,
    ...rest
}) => {
    const th = useTheme() as any
    const lg = th?.screens?.lg ?? '1024px'
    const [topId, setTopId] = useState<string | null>(null)

    const idRef = useRef<string>(
        `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
    )
    if (typeof idRef.current !== 'string') {
        idRef.current = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
    }

    // Palette by tone (falls back to theme when available)
    const tonePalette: Record<
        InfoTone,
        {
            main: string
            contrast: string
            surface: string
            border: string
            overlay: string
        }
    > = {
        warning: {
            main: th?.colors?.eyeCatcher ?? '#ff8c1c',
            contrast: th?.colors?.white ?? '#fff',
            surface: th?.colors?.whiteWithOpacity ?? 'rgba(255,255,255,.98)',
            border: th?.colors?.lightBlue ?? '#c7dff8',
            overlay: th?.colors?.blackWithOpacity ?? 'rgba(0,0,0,.45)'
        },
        info: {
            main: th?.colors?.info ?? th?.colors?.blue ?? '#2A81F6',
            contrast: th?.colors?.white ?? '#fff',
            surface: th?.colors?.whiteWithOpacity ?? 'rgba(255,255,255,.98)',
            border: th?.colors?.lightBlue ?? '#c7dff8',
            overlay: th?.colors?.blackWithOpacity ?? 'rgba(0,0,0,.45)'
        },
        success: {
            main: th?.colors?.success ?? th?.colors?.green ?? '#1BAA66',
            contrast: th?.colors?.white ?? '#fff',
            surface: th?.colors?.whiteWithOpacity ?? 'rgba(255,255,255,.98)',
            border: th?.colors?.lightGreen ?? '#bfe7d3',
            overlay: th?.colors?.blackWithOpacity ?? 'rgba(0,0,0,.45)'
        },
        error: {
            main: th?.colors?.error ?? th?.colors?.red ?? '#E24A4A',
            contrast: th?.colors?.white ?? '#fff',
            surface: th?.colors?.whiteWithOpacity ?? 'rgba(255,255,255,.98)',
            border: th?.colors?.lightRed ?? '#f6c9c9',
            overlay: th?.colors?.blackWithOpacity ?? 'rgba(0,0,0,.45)'
        }
    }

    const palette = tonePalette[tone]

    // Infer icon if none provided
    const inferredIcon = (() => {
        if (customIcon) return customIcon
        if (tone === 'warning' || tone === 'error')
            return <Icon icon={icon.Warning} />
        if (kind === 'marketing') return <Icon icon={icon.Gift ?? icon.Info} />
        return <Icon icon={icon.Info} />
    })()

    // Body scroll lock when open
    // useEffect(() => {
    //   if (!open) return
    //   const body = document.body
    //   const prevOverflow = body.style.overflow
    //   body.style.overflow = 'hidden'
    //   const preventTouch = (e: TouchEvent) => e.preventDefault()
    //   document.addEventListener('touchmove', preventTouch, { passive: false })
    //   return () => {
    //     body.style.overflow = prevOverflow
    //     document.removeEventListener('touchmove', preventTouch)
    //   }
    // }, [open])

    // Keyboard handling + focus
    const firstInteractiveRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const onState = (e: Event) => {
            const { topId } = (e as CustomEvent<{ topId: string | null }>)
                .detail
            setTopId(topId)
        }
        window.addEventListener(INFO_TOAST_STATE_CHANGE, onState)
        return () =>
            window.removeEventListener(INFO_TOAST_STATE_CHANGE, onState)
    }, [])

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !disableEscapeClose) {
                e.preventDefault()
                onClose?.()
            }
        }
        window.addEventListener('keydown', onKey)
        // move focus to first interactive control
        const id = window.setTimeout(
            () => firstInteractiveRef.current?.focus(),
            0
        )
        return () => {
            window.removeEventListener('keydown', onKey)
            window.clearTimeout(id)
        }
    }, [open, disableEscapeClose, onClose])

    useEffect(() => {
        const id = idRef.current
        if (open) notifyToastOpen(id, tone)
        else notifyToastClose(id)
        return () => notifyToastClose(id)
    }, [open, tone])

    const isTop = idRef.current === topId
    const shouldAlwaysShowOnMobile = kind === 'system'
    const visible = open && (!isMobile() || isTop || shouldAlwaysShowOnMobile)
    if (!visible) return null

    const wrap = css`
        width: min(92vw, 560px);
        pointer-events: auto;
        opacity: ${open ? 1 : 0};
        transition:
            opacity 180ms ease,
            transform 200ms ease;
        transform: translateY(${open ? '0' : '-8px'});

        @media (min-width: ${lg}) {
            width: 420px;
        }
    `

    const box = css`
        position: relative;
        background: ${palette.surface};
        color: ${th?.fontColor?.primary ?? '#1a1a1a'};
        border: 2px solid ${palette.border};
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
        box-sizing: border-box;
        padding: ${th?.spacing?.md ?? '16px'};
        max-width: 100%;
        max-height: 80vh;
        overflow: auto;
    `

    const close = css`
        position: absolute;
        top: 6px;
        right: 6px;
        width: 28px;
        height: 28px;
        display: grid;
        place-items: center;
        background: transparent;
        border: none;
        border-radius: 0;
        color: ${palette.main};
        cursor: pointer;
        font-size: 16px;
        line-height: 1;
        opacity: 0.95;
        &:hover {
            opacity: 1;
        }
    `

    const titleCss = css`
        margin: 0 0 6px;
        font-size: 20px;
        font-weight: 800;
        color: ${palette.main};
    `

    const iconCss = css`
        font-size: 36px;
        line-height: 1;
        margin-top: -6px;
        color: ${palette.main};
    `

    const row = css`
        display: grid;
        grid-template-columns: 40px 1fr;
        gap: 12px;
        align-items: start;
    `

    const textBody = css`
        margin: 0;
        line-height: 1.45;
        font-size: 16px;
        font-weight: 400;

        p {
            margin: 0 0 8px;
        }

        strong {
            font-weight: 700;
        }

        p:last-of-type {
            margin-bottom: 0;
        }
    `

    const ctaPrimary = css`
        padding: 10px 18px;
        background: ${palette.main};
        color: ${palette.contrast};
        border: 1px solid ${palette.main};
        border-radius: 0;
        font-weight: 700;
        font-size: 16px;
        cursor: pointer;
    `

    const ctaWrap = css`
        display: flex;
        justify-content: center;
        gap: 12px;
        margin-top: 14px;
        flex-wrap: wrap;
    `

    const ctaLink = css`
        background: transparent;
        border: none;
        color: ${palette.main};
        text-decoration: underline;
        cursor: pointer;
        font-weight: 700;
    `

    if (!open) return null

    const priority = tonePriority[tone]
    const root = getToastStackRoot()
    if (!root) return null

    return createPortal(
        <div
            css={wrap}
            style={{
                order: -priority
            }}
            aria-live="polite"
            {...rest}
        >
            <div css={box} role="dialog" onClick={e => e.stopPropagation()}>
                <button
                    type="button"
                    aria-label="Close"
                    css={close}
                    onClick={onClose}
                    ref={firstInteractiveRef}
                >
                    <Icon icon={icon.Close} />
                </button>

                <div css={row}>
                    <span css={iconCss}>{inferredIcon}</span>
                    <div>
                        <h2 css={titleCss}>{title}</h2>
                        <div css={textBody}>{text}</div>
                    </div>
                </div>

                {(primaryCta || (secondaryCtas && secondaryCtas.length)) && (
                    <div css={ctaWrap}>
                        {primaryCta && (
                            <button
                                css={ctaPrimary}
                                onClick={primaryCta.onClick}
                                aria-label={
                                    primaryCta.ariaLabel ??
                                    String(primaryCta.label)
                                }
                            >
                                {primaryCta.label}
                            </button>
                        )}
                        {secondaryCtas?.map((b, i) => (
                            <button
                                key={i}
                                css={ctaLink}
                                onClick={b.onClick}
                                aria-label={b.ariaLabel ?? String(b.label)}
                            >
                                {b.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>,
        root
    )
}

export default InfoToaster
